import type {CampaignCopy} from "i18n/en/campaign";
import {useEffect, useMemo, useRef, type RefObject} from "react";
import {CampaignCursorBadge} from "./CampaignCursorBadge";
import {useTheme} from "styled-components";
import {ANIMATED_EDITIONS, campaignRenderers} from "./campaignRenderers";
import {campaignPalette} from "./campaignPalette";
import type {CampaignFrame, CampaignId} from "./campaignPlayback";
import {ArtHitArea, ArtCanvas, ArtworkLayer} from "./LandingCampaign.styles";

function ArtworkPlate({id, outgoing, paused, readingRef, campaign}: {campaign: CampaignCopy; id: CampaignId; outgoing: boolean; paused: boolean; readingRef: RefObject<HTMLDivElement | null>}) {
	const canvas = useRef<HTMLCanvasElement>(null);
	const motionTime = useRef(0);
	const {tokens} = useTheme();
	const copy = campaign.artwork;
	const palette = useMemo(() => campaignPalette(tokens, id), [tokens, id]);
	useEffect(() => {
		const node = canvas.current;
		const parent = node?.parentElement;
		const context = node?.getContext("2d");
		if (!node || !parent || !context) return;
		// Both surfaces own a stable column, even when its loading or resolved card is replaced.
		const card = parent.closest("[data-campaign-surface]")?.querySelector("[data-drop-container]");
		let width = 0, height = 0, readingTop = 0, readingRight = 0, composerLeft = 0;
		let composerTop: number | undefined, composerBottom: number | undefined;
		let wash: CanvasGradient | null = null;
		let frame = 0, disposed = false;
		const media = window.matchMedia("(prefers-reduced-motion: reduce)");
		const paint = () => {
			if (!width || !height) return;
			campaignRenderers[id](context, width, height, 1, palette, {time: motionTime.current, readingTop, readingRight, composerLeft, composerTop, composerBottom, copy});
			if (wash) {
				context.fillStyle = wash;
				context.fillRect(0, 0, width, height);
			}
		};
		const measure = () => {
			if (disposed) return;
			const bounds = parent.getBoundingClientRect();
			width = bounds.width; height = bounds.height;
			if (!width || !height) return;
			const reading = readingRef.current?.getBoundingClientRect();
			readingTop = (reading?.top ?? bounds.top) - bounds.top;
			readingRight = (reading?.right ?? bounds.left) - bounds.left;
			const composer = (card?.querySelector("[data-create-card]") ?? card)?.getBoundingClientRect();
			composerLeft = (composer?.left ?? bounds.right) - bounds.left;
			composerTop = composer ? composer.top - bounds.top : undefined;
			composerBottom = composer ? composer.bottom - bounds.top : undefined;
			const ratio = Math.min(window.devicePixelRatio || 1, 2);
			node.width = Math.round(width * ratio);
			node.height = Math.round(height * ratio);
			context.setTransform(ratio, 0, 0, ratio, 0, 0);
			wash = null;
			if (width <= 900 && reading) {
				// Fade across the reading width. Cache the geometry and gradient, never read layout per frame.
				wash = context.createLinearGradient(0, Math.max(0, readingTop - height * .15), 0, Math.max(1, readingTop));
				wash.addColorStop(0, `${palette.ground}00`);
				wash.addColorStop(1, palette.ground);
			}
			paint();
		};
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(parent);
		if (readingRef.current) observer.observe(readingRef.current);
		if (card) observer.observe(card);
		void document.fonts.ready.then(measure);
		// Pauses preserve the pigment phase; a new effect starts its delta clock at resume, not at mount.
		if (!paused && !media.matches && ANIMATED_EDITIONS.has(id)) {
			let last = performance.now();
			const tick = (now: number) => {
				if (media.matches || document.hidden) return;
				motionTime.current += Math.max(0, now - last);
				last = now;
				paint();
				frame = requestAnimationFrame(tick);
			};
			frame = requestAnimationFrame(tick);
		}
		return () => { disposed = true; observer.disconnect(); cancelAnimationFrame(frame); };
	}, [id, palette, readingRef, paused, copy]);
	return <ArtCanvas ref={canvas} aria-hidden="true" $outgoing={outgoing} $paused={paused}/>;
}

/** Two decorative plates dissolve; neither plate ever contains the sender's form or navigation. */
export function CampaignArtwork({active, frame, paused, onOpen, readingRef, campaign}: {campaign: CampaignCopy; readingRef: RefObject<HTMLDivElement | null>; active: boolean; frame: CampaignFrame; paused: boolean; onOpen: () => void}) {
	const hitAreaRef = useRef<HTMLDivElement>(null);
	return (
		<>
		<ArtworkLayer $scene={frame.active} data-campaign-art inert={!active}>
			<ArtworkPlate campaign={campaign} readingRef={readingRef} key={frame.active} id={frame.active} outgoing={false} paused={paused}/>
			{frame.outgoing && <ArtworkPlate campaign={campaign} readingRef={readingRef} key={frame.outgoing} id={frame.outgoing} outgoing paused={paused}/>}
			{/* The visible CTA is the accessible entry. This decorative pointer shortcut is not a page-sized form control. */}
			<ArtHitArea ref={hitAreaRef} aria-hidden="true" onClick={onOpen}/>
		</ArtworkLayer>
		{/* Outside the artwork stacking context so the headline cannot paint over the badge. */}
		<CampaignCursorBadge campaign={campaign} id={frame.active} active={active} hitAreaRef={hitAreaRef}/>
	</>);
}
